import { FC, useEffect } from 'react';
import useSWR, { Fetcher } from 'swr';
import { shallow } from 'zustand/shallow';

import AuthService from '@/services/AuthService';
import { getOrdersIgnoreError } from '@/services/OrderServiceClient';
import { authStore, getUserAuthDataFromCookies } from '@/stores/authStore';
import { pushToDataLayerWithEnhancedConversion } from '@/utils/pushToDataLayerWithEnhancedConversion';

import { liveChatStore } from '../liveChatStore';

const DEFAULT_FOCUS_THROTTLE = 60 * 1000;

export const AuthManager: FC = () => {
  const {
    isUserLoggedIn,
    userData,
    userAuthDataLoading,
    setUserDataLoading,
    setUserAuthData,
    setUserData,
    setUserOrdersLoading,
    setUserOrders,
  } = authStore((state) => ({
    isUserLoggedIn: state.isUserLoggedIn,
    userData: state.userData,
    userAuthDataLoading: state.userAuthDataLoading,
    setUserDataLoading: state.setUserDataLoading,
    setUserAuthData: state.setUserAuthData,
    setUserData: state.setUserData,
    setUserOrdersLoading: state.setUserOrdersLoading,
    setUserOrders: state.setUserOrders,
  }));

  const { setChatUser } = liveChatStore(
    ({ setChatUser }) => ({
      setChatUser,
    }),
    shallow,
  );

  useEffect(() => {
    const userAuthData = getUserAuthDataFromCookies();
    setUserAuthData(userAuthData);
  }, []);

  const loadUserProfile: Fetcher<Awaited<ReturnType<typeof AuthService.getProfile> | null>> = async () => {
    setUserDataLoading();
    try {
      // Load the user's profile
      const result = await AuthService.getProfile();
      const name = `${result.firstName} ${result.lastName}`;
      setUserData(result);
      hubspotIdentifyUser(result.email);
      gtmIdentifyUser(result.email, result.phone);
      setChatUser({ name, email: result.email });
      return result;
    } catch (error) {
      console.error('Error loading user profile', error);
      // If it fails, log out
      setUserData(null);
      window.location.href = `/api/auth/signout?redirect=${encodeURIComponent(window.location.pathname)}`;
    }

    return null;
  };

  const loadOrdersData: Fetcher<Awaited<ReturnType<typeof getOrdersIgnoreError>>> = async () => {
    setUserOrdersLoading();
    // Orders API call
    const result = await getOrdersIgnoreError();
    setUserOrders(result ?? []);
    return result;
  };

  useSWR(isUserLoggedIn && !userAuthDataLoading ? 'AuthManager-/me/profile' : null, loadUserProfile, {
    revalidateOnFocus: true,
    focusThrottleInterval: DEFAULT_FOCUS_THROTTLE,
  });

  // Handle loading and refreshing of the cart API data
  useSWR(isUserLoggedIn && userData && !userAuthDataLoading ? 'AuthManager-/me/orders' : null, loadOrdersData, {
    revalidateOnFocus: true,
    focusThrottleInterval: DEFAULT_FOCUS_THROTTLE,
  });

  return null;
};

const hubspotIdentifyUser = (email: string) => {
  const _hsq = (globalThis.window._hsq = globalThis.window._hsq ?? []);
  _hsq.push(['identify', { email }]);
};

const gtmIdentifyUser = (email: string, phone: string = '') => {
  pushToDataLayerWithEnhancedConversion(email, phone, {
    event: 'user_authenticated',
  });
};
